import { FC } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Button,
  CardActions,
  Chip
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CodeIcon from '@mui/icons-material/Code';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskIcon from '@mui/icons-material/Task';
// import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

interface Tool {
  name: string;
  description: string;
  icon: JSX.Element;
  link: string;
}

const tools: Tool[] = [
  {
    name: 'Simulate UI',
    description: 'Easily simulate and test redirect flows during development to ensure a smooth and predictable user journey.',
    icon: <CodeIcon fontSize="large" />,
    link: '/simulate-ui'
  },
  {
    name: 'Release Planner',
    description: 'The Release Planner application enables seamless communication and coordination of release plans across all peer products, ensuring a smooth and efficient deployment process.',
    icon: <BuildIcon fontSize="large" />,
    link: '/release-planner'
  },
  {
    name: 'Monthly Planner',
    description: 'Plan tasks across a monthly calendar. Add, import/export, and visualize progress.',
    icon: <CalendarMonthIcon fontSize="large" />,
    link: '/monthly-planner'
  },
  {
    name: 'Bucket List',
    description: 'Manage your daily bucket list',
    icon: <TaskIcon fontSize="large" />,
    link: '/daily-tasks'
  }
  // {
  //   name: 'Roadmap Builder',
  //   description: 'Visually plan and communicate your product or project roadmap.',
  //   icon: <TimelineIcon fontSize="large" />,
  //   link: '/roadmap-builder'
  // }
];

const Home: FC = () => {
  const theme = useTheme();

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          py: 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                width: '150%',
                height: '150%',
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                zIndex: -1,
                animation: 'pulse 3s infinite',
              },
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(0.95)',
                  opacity: 0.5,
                },
                '50%': {
                  transform: 'scale(1.05)',
                  opacity: 0.8,
                },
                '100%': {
                  transform: 'scale(0.95)',
                  opacity: 0.5,
                },
              },
            }}
          >
            <Box
              component="img"
              src="/assets/Single_mainlogo_tm.svg"
              alt="Logo"
              sx={{
                height: { xs: 60, sm: 80 },
                filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.1))',
                transition: 'all 0.4s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.08) translateY(-2px)',
                  filter: `drop-shadow(0 4px 12px ${alpha(theme.palette.primary.main, 0.3)})`,
                },
              }}
              onClick={() => window.location.href = '/'}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          minHeight: '100vh',
          pt: { xs: 12, sm: 14 },
          pb: 8,
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 6,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 100,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: 2,
              }
            }}
          >
            Toolbox
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {tools.map((tool: Tool) => (
              <Grid item xs={12} sm={6} key={tool.link}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                    }}
                  >
                    <IconButton
                      sx={{
                        mb: 2,
                        p: 2,
                        background: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      {tool.icon}
                    </IconButton>
                    <Typography
                      variant="h5"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {tool.name}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ flexGrow: 1 }}
                    >
                      {tool.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      fullWidth
                      href={tool.link}
                      sx={{
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        transition: 'transform 0.2s',
                        '&:hover': {
                          background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                          transform: 'scale(1.02)',
                        },
                      }}
                    >
                      Access Tools
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Give Back Section */}
          <Card
            sx={{
              mt: 6,
              mb: 4,
              background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)}, ${alpha(theme.palette.success.main, 0.1)})`,
              border: `1px solid ${alpha('#FFD700', 0.3)}`,
              borderRadius: '15px',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: `linear-gradient(90deg, #FFD700, ${theme.palette.success.main}, #FFD700)`,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(45deg, #FFD700, ${theme.palette.success.main})`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  💛 Donate Back – Not Just Money
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    mb: 1,
                  }}
                >
                  If Release Pilot boosted your productivity by 0.1%—give back through <strong>impact, not money</strong> 🌟
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2,
                }}
              >
                {['🤝 Help others', '👥 Mentor fellow developer', '🚀 Share opportunities', '💡 Contribute skills', '❤️ Spread kindness'].map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: alpha('#FFD700', 0.5),
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: alpha('#FFD700', 0.1),
                        transform: 'scale(1.02)',
                      },
                    }}
                  />
                ))}
              </Box>

              <Typography 
                variant="body2" 
                sx={{ 
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                }}
              >
                Every small action matters. Let your gratitude become someone else's breakthrough. 🙌
              </Typography>
            </CardContent>
          </Card>

          {/* Crafted with Love */}
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: theme.palette.text.secondary,
              mt: 3,
              mb: 2,
              fontSize: '1.1rem',
            }}
          >
            Crafted with ❤️ in India
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default Home;
